import { inject, Injectable, signal } from '@angular/core';
import { point } from '@turf/turf';
import { Feature, FeatureCollection, GeoJSON, GeoJsonProperties, Geometry, LineString, Point } from 'geojson';
import {
    AddLayerObject,
    GeoJSONSource,
    LayerSpecification,
    LngLat,
    LngLatBounds,
    LngLatLike,
    Map as MapLibreMap,
    MapGeoJSONFeature,
    PointLike,
    SourceSpecification,
    StyleSpecification,
} from 'maplibre-gl';
import { v4 as uuidv4 } from 'uuid';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    MapBaseSource,
    MapboxStyleWithZoom,
    MapComponent,
    MapFieldInterventionSource,
    MapGeomSource,
    MapLayerSource,
    MapSourceTypeEnum,
} from '@connect-services/client/components/map/map.component';
import { AssertService } from '@connect-services/client/services/assert.service';
import {
    BasemapInfo,
    BasemapLayerType,
    BASEMAPS_CATALOG,
    TERRAIN_SOURCE_ID,
    TileSourceTypeEnum,
} from '@connect-services/client/services/basemap.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { GeometryEditPreviewService } from '@connect-services/client/services/geometry-edit-preview.service';
import {
    MASK_LAYER_ID,
    MASK_OUTLINE_LAYER_ID,
    MASK_OUTLINE_SOURCE_ID,
    MASK_SOURCE_ID,
} from '@connect-services/client/services/map/mask/map-mask.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import {
    CONNIE_LAYER_ID,
    CONNIE_SOURCE_ID,
    DRAWN_POLYGON_SOURCE_ID,
} from '@connect-services/client/services/map-draw.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { MapFieldInterventionService } from '@connect-services/client/services/map-field-intervention.service';
import { MapFilterService } from '@connect-services/client/services/map-filter.service';
import { MapLayerService } from '@connect-services/client/services/map-layer.service';
import { MapLayerStorageService } from '@connect-services/client/services/map-layer-storage.service';
import type { MapGeoJSONFeatureWithLayer } from '@connect-services/client/services/map-popup.service';
import { MeasurementToolService } from '@connect-services/client/services/measurement-tool.service';
import {
    NETWORK_PATH_ALL_ROUTES_SOURCE_ID,
    NETWORK_PATH_EDGE_SOURCE_ID,
    NETWORK_PATH_NODE_SOURCE_ID,
} from '@connect-services/client/services/network-path/network-path-map.service';
import { SelectZoneLayerService } from '@connect-services/client/services/select-zone-layer.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import {
    Geometry as SdkGeometry,
    GeometryCoordinates,
    Layer,
    LayerFilterResult,
    LineString as SdkLineString,
    MapboxStyle,
    MultiLineString,
    MultiPoint,
    MultiPolygon,
    Point as SdkPoint,
    Polygon,
} from '@connect-services/src/sdk/api';

export type DrawSourcesOptions = {
    beforeId?: string;
    fitBounds: boolean;
    geohashes?: string[];
    intersectsFieldInterventionArea?: string;
    zoneDelemiter?: string;
};

export class SourcesZoom {
    public sourcesLegend: boolean = true;
    public sourcesPanel: boolean = true;
}

export const DEFAULT_SOURCES_ZOOM: SourcesZoom = {
    sourcesLegend: true,
    sourcesPanel: true,
};

export const POINT_INDEX = 'pointIndex';

type EditsVertex = {
    plusCoordinates: Feature<Point, GeoJsonProperties>[];
    vertexLayers: Feature<Point, GeoJsonProperties>[];
};

type EditVertexOptions = { isAddPolygonVertex: boolean; isRingIndex: boolean; onlyFirstAndLast: boolean };

type EditVertexEntityContext = {
    businessIdentifier?: string;
    entityId: string;
    layerId: string;
    layerName: string;
};

const LIBERTY_SOURCE_IDS = ['openmaptiles', 'ne2_shaded'];
const PERSISTENT_LAYER_IDS = [MASK_LAYER_ID, MASK_OUTLINE_LAYER_ID];
const PERSISTENT_SOURCE_IDS = [
    MASK_SOURCE_ID,
    MASK_OUTLINE_SOURCE_ID,
    NETWORK_PATH_NODE_SOURCE_ID,
    NETWORK_PATH_EDGE_SOURCE_ID,
    NETWORK_PATH_ALL_ROUTES_SOURCE_ID,
];

type SvgIcon = { name: string; url: string };

@Injectable({
    providedIn: 'root',
})
export class MapSourceService extends MapBaseService {
    public static readonly checkSourcesVisibility = signal<boolean>(true);
    public static readonly checkSourcesZoom = signal<SourcesZoom>(DEFAULT_SOURCES_ZOOM);
    public static readonly defaultZoomSensibility = 5;
    public static readonly isMapOrProjectFirstLoading = signal<boolean>(true);
    public static readonly sourcesActivationChanged = signal<boolean>(false);

    private static defaultDrawSourcesOptions: DrawSourcesOptions = { fitBounds: false };

    private readonly editModeStateService = inject(EditModeStateService);
    private readonly geometryEditPreviewService = inject(GeometryEditPreviewService);
    private readonly mapFieldInterventionService = inject(MapFieldInterventionService);
    private readonly mapFilterService = inject(MapFilterService);
    private readonly mapLayerService = inject(MapLayerService);
    private readonly mapLayerStorageService = inject(MapLayerStorageService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly selectZoneLayerService = inject(SelectZoneLayerService);
    private readonly sourcesService = inject(SourcesService);
    private readonly svgIcons: SvgIcon[] = [
        { name: 'edit-vertex-icon', url: './assets/images/icons/vertex.svg' },
        { name: 'circle-outline-svg', url: './assets/images/icons/circle-outline.svg' },
        { name: 'circle-svg', url: './assets/images/icons/circle.svg' },
        { name: 'edit-plus-outline-icon', url: './assets/images/icons/plus-outline.svg' },
        { name: 'edit-plus-icon', url: './assets/images/icons/plus.svg' },
    ];

    public constructor() {
        super();
    }

    public static isSourceRenderable(source: MapLayerSource): boolean {
        return !(!source.isVisible || !source.isActivated || source.isFiltered);
    }

    public addCreationLayer(feature: Feature<LineString>): void {
        if (!this.map) {
            return;
        }

        if (this.map.getLayer(MapEditModeService.CREATE_GEOMETRY_LAYER)) {
            this.map.removeLayer(MapEditModeService.CREATE_GEOMETRY_LAYER);
        }

        this.addGeometryCreationSource(feature);

        this.map.addLayer(MapEditModeService.LAYER_SPECIFICATION_CREATION_GEOMETRY, undefined);
    }

    public addCustomLayer(options: AddLayerObject): void {
        if (!this.map) {
            return;
        }

        const existingLayer = this.map.getLayer(options.id);
        if (!existingLayer) {
            this.map.addLayer(options);
        }
    }

    public addCustomSource(sourceId: string, features: Feature[] = []): void {
        if (!this.map) {
            return;
        }

        this.map.addSource(sourceId, {
            data: {
                features: features,
                type: 'FeatureCollection',
            },
            type: 'geojson',
        });
    }

    public addPhantomLineLayer(
        phantomSource: GeoJSONSource | null,
        sourceId: string,
        layerId: string,
        lineColor: string,
    ): void {
        if (!this.map || !phantomSource || !this.map.getSource(sourceId) || this.map.getLayer(layerId)) {
            return;
        }

        const options: AddLayerObject = {
            id: layerId,
            layout: {},
            paint: {
                'line-color': lineColor,
                'line-dasharray': [2, 2],
                'line-width': 2,
            },
            source: sourceId,
            type: 'line',
        };

        this.addCustomLayer(options);
    }

    public addPhantomPolygonLayer(
        phantomSource: GeoJSONSource | null,
        sourceId: string,
        layerId: string,
        lineColor: string,
    ): void {
        if (!phantomSource) {
            return;
        }

        const options: AddLayerObject = {
            id: layerId,
            layout: {},
            paint: {
                'fill-color': lineColor,
                'fill-opacity': 0.3,
            },
            source: sourceId,
            type: 'fill',
        };

        this.addCustomLayer(options);
    }

    public async applyEditStyle(sourceIds: string[], entityId?: string): Promise<void> {
        if (this.map!.getLayer(MapEditModeService.EDIT_LAYER)) {
            this.map!.removeLayer(MapEditModeService.EDIT_LAYER);
        }
        if (this.map!.getLayer(MapEditModeService.EDIT_ADD_VERTEX_LAYER)) {
            this.map!.removeLayer(MapEditModeService.EDIT_ADD_VERTEX_LAYER);
        }

        const isLineType = await this.addMapEditSource(sourceIds, entityId);

        this.addMapVertexIcon();

        this.map!.addLayer(MapEditModeService.MAP_LAYER, undefined);
        this.map!.addLayer(
            isLineType
                ? MapEditModeService.LAYER_SPECIFICATION_ADD_LINE_VERTEX
                : MapEditModeService.LAYER_SPECIFICATION_ADD_POLYGON_VERTEX,
            undefined,
        );
    }

    public async applySnapStyle(sourceIds: string[]): Promise<void> {
        if (this.map!.getLayer(MapEditModeService.EDIT_SNAP_LAYER)) {
            this.map!.removeLayer(MapEditModeService.EDIT_SNAP_LAYER);
        }

        if (this.map!.getSource(MapEditModeService.EDIT_SNAP_SOURCE)) {
            this.map!.removeSource(MapEditModeService.EDIT_SNAP_SOURCE);
        }

        const editLayerFeature = await this.setupVertexLayer(sourceIds);

        this.map!.addSource(MapEditModeService.EDIT_SNAP_SOURCE, {
            data: {
                features: editLayerFeature.vertexLayers,
                type: 'FeatureCollection',
            },
            type: 'geojson',
        });

        this.map!.addLayer(MapEditModeService.LAYER_SPECIFICATION_SNAP, undefined);
    }

    public bindMap(map: MapLibreMap): void {
        this.map = map;
    }

    public clearSources(backgroundSourceId: string, exceptSourceList: MapBaseSource<unknown>[]): void {
        if (!this.isMapPresent()) {
            return;
        }

        const sourceIds = exceptSourceList.flatMap((source: MapBaseSource<unknown>) => {
            return source.sourceId;
        });

        // This is to exclude the measurement source from the list of sources to be removed when moving the map
        sourceIds.push(MeasurementToolService.measurementSourceId, DRAWN_POLYGON_SOURCE_ID);
        sourceIds.push(
            ...[
                MapEditModeService.EDIT_SOURCE,
                MapEditModeService.EDIT_ADD_VERTEX_SOURCE,
                MapEditModeService.EDIT_SNAP_SOURCE,
                MapEditModeService.CREATE_GEOMETRY_SOURCE,
                MapEditModeService.PREVIEW_GEOMETRY_SOURCE,
                CONNIE_SOURCE_ID,
                TERRAIN_SOURCE_ID,
                ...LIBERTY_SOURCE_IDS,
                ...PERSISTENT_SOURCE_IDS,
            ],
        );

        const mapStyle: StyleSpecification = this.map.getStyle();
        if (!mapStyle) {
            return;
        }

        const basemapIds = new Set<string>(
            Object.values(BASEMAPS_CATALOG).map((base: BasemapInfo): TileSourceTypeEnum => base.type),
        );

        mapStyle.layers.forEach((layer: LayerSpecification) => {
            if (PERSISTENT_LAYER_IDS.includes(layer.id)) {
                return;
            }

            if (
                basemapIds.has(layer.id) ||
                (layer.type !== 'background' &&
                    (sourceIds.includes(layer.source) ||
                        this.selectZoneLayerService
                            .selectedSources()
                            .some((source) => source.sourceId === layer.source)))
            ) {
                return;
            }

            this.map.removeLayer(layer.id);
        });

        const basemapSourceIds = new Set<string>(
            Object.values(BASEMAPS_CATALOG).reduce<TileSourceTypeEnum[]>(
                (acc: TileSourceTypeEnum[], base: BasemapInfo): TileSourceTypeEnum[] => {
                    if (base.layerType === BasemapLayerType.BACKGROUND) {
                        return acc;
                    }
                    acc = [...acc, base.type];

                    return acc;
                },
                [],
            ),
        );

        for (const source in mapStyle.sources) {
            if (basemapSourceIds.has(source)) {
                continue;
            }

            if (
                source !== backgroundSourceId &&
                !sourceIds.includes(source) &&
                !this.selectZoneLayerService
                    .selectedSources()
                    .map((source) => source.sourceId)
                    .includes(source)
            ) {
                this.map.removeSource(source);
            }
        }
    }

    public async disableSourcesZoom(
        sourceIds: string[],
        fitBounds: boolean = true,
        zoneDelemiter?: string,
    ): Promise<void> {
        const sourcesClearedZoom: MapBaseSource<unknown>[] = this.mapLayerService.removeAllZoom(
            this.sourcesService.sources().filter((source) => sourceIds.includes(source.sourceId)),
        );

        await this.drawSources(
            sourcesClearedZoom,
            {
                fitBounds,
                zoneDelemiter,
            },
            true,
        );
    }

    public async drawConnieGeojson(geojson: FeatureCollection): Promise<void> {
        if (!this.map) {
            return;
        }

        const existing = this.map.getSource(CONNIE_SOURCE_ID) as GeoJSONSource | undefined;

        if (existing) {
            existing.setData(geojson);

            return;
        }

        const layerStyle: MapboxStyleWithZoom = await this.sourcesService.getLayerStyle(
            'ea2acd83-3a5e-44a3-b9ca-c1bd74c4883b', // Random UUID to get default style
        );

        this.map.addSource(CONNIE_SOURCE_ID, {
            data: geojson,
            type: 'geojson',
        });

        layerStyle.style.forEach((style: MapboxStyle): void => {
            this.map?.addLayer({
                ...style,
                id: `${CONNIE_LAYER_ID}_${style.id}`,
                source: CONNIE_SOURCE_ID as unknown as SourceSpecification,
                type: style.type as LayerSpecification['type'],
            });
        });
    }

    public async drawSources(
        sources: MapBaseSource<unknown>[],
        drawOptions: DrawSourcesOptions,
        isEditModeActive: boolean = false,
    ): Promise<void> {
        const options: DrawSourcesOptions = { ...MapSourceService.defaultDrawSourcesOptions, ...drawOptions };

        if (!this.isMapPresent()) {
            return;
        }

        if (options.fitBounds) {
            this.mapBounds = new LngLatBounds();
        }

        sources = this.orderSources(sources);

        let prevSourceId = '';

        for (const source of sources) {
            switch (source.type) {
                case MapSourceTypeEnum.FIELD_INTERVENTION: {
                    await this.mapFieldInterventionService.drawFieldIntervention(source as MapFieldInterventionSource, {
                        ...options,
                        ...(prevSourceId && { beforeId: prevSourceId }),
                    });
                    break;
                }
                case MapSourceTypeEnum.GEOM: {
                    this.drawGeom(source as MapGeomSource);
                    break;
                }
                case MapSourceTypeEnum.LAYER: {
                    await this.mapLayerService.drawLayer(
                        source as MapLayerSource,
                        {
                            ...options,
                            ...(prevSourceId && { beforeId: prevSourceId }),
                        },
                        isEditModeActive,
                    );
                    break;
                }
            }

            prevSourceId = source.sourceId;
        }

        if (!options.fitBounds) {
            return;
        }

        if (!MapBaseService.isFitBoundNeeded()) {
            MapBaseService.isFitBoundNeeded.set(true);

            return;
        }
        this.fitMapBounds();
    }

    public fitMapBounds(): void {
        const bbox = this.projectBloc.value()?.bbox;

        if (
            (!bbox && (!this.mapBounds || this.mapBounds.isEmpty())) ||
            !this.isMapPresent() ||
            !MapSourceService.isMapOrProjectFirstLoading()
        ) {
            return;
        }

        const bounds = new LngLatBounds();

        if (bbox) {
            bbox.coordinates[0].forEach(([lng, lat]: number[]): void => {
                bounds.extend([lng, lat]);
            });
        }

        this.map.fitBounds(bounds.isEmpty() ? this.mapBounds! : bounds, MapComponent.fitBoundsOptions, {
            fitBounds: true,
        });

        MapSourceService.isMapOrProjectFirstLoading.set(false);
        this.mapBounds = undefined;
    }

    public getRenderedEntityFeature(
        layerId: string,
        entityId: string,
        geom: SdkGeometry,
    ): MapGeoJSONFeatureWithLayer | undefined {
        if (!this.map?.getSource(layerId) || !this.map.isSourceLoaded(layerId)) {
            return undefined;
        }

        const source: MapBaseSource<unknown> | undefined = this.sourcesService.getSource(layerId);

        if (!source) {
            return undefined;
        }

        const featurePoint = this.map.project(this.extractOnePointInFeature(geom.coordinates));

        const radius = MapSourceService.defaultZoomSensibility;

        const bbox: [PointLike, PointLike] = [
            [featurePoint.x - radius, featurePoint.y - radius],
            [featurePoint.x + radius, featurePoint.y + radius],
        ];

        const feature: MapGeoJSONFeature | undefined = this.map
            .queryRenderedFeatures(bbox)
            .find(
                (feature: MapGeoJSONFeature) => feature.source === layerId && feature.properties?.['uuid'] === entityId,
            );

        if (!feature) {
            return undefined;
        }

        return Object.assign({}, feature, {
            geometry: feature.geometry,
            layer: {
                ...feature.layer,
                name: source.name,
            },
        });
    }

    public isSourceInZoomRange(source: MapBaseSource<unknown>): boolean {
        if (!this.isMapPresent()) {
            return true;
        }

        return this.isZoomIncluded(source.maxzoom, source.minzoom);
    }

    public isSourceVisible(source: MapBaseSource<unknown>): boolean {
        if (source instanceof MapFieldInterventionSource) {
            return this.mapFieldInterventionService.isSourceVisible(source);
        }

        return this.mapLayerService.isSourceVisible(source as MapLayerSource);
    }

    /**
     * Order sources by their defined order, prioritize field interventions
     * @param sources
     */
    public orderSources(sources: MapBaseSource<unknown>[]): MapBaseSource<unknown>[] {
        return sources.toSorted((a: MapBaseSource<unknown>, b: MapBaseSource<unknown>) => {
            if (a instanceof MapFieldInterventionSource) {
                return -1;
            }

            if (b instanceof MapFieldInterventionSource) {
                return 1;
            }

            if (a instanceof MapLayerSource && b instanceof MapLayerSource) {
                return b.data.order - a.data.order;
            }

            return 0;
        });
    }

    public async redrawSources(): Promise<void> {
        const layerIds: string[] = this.sourcesService
            .sources()
            .map((source: MapBaseSource<unknown>): string => source.sourceId);

        this.mapLayerStorageService.resetLayerGeohash(layerIds);
        await this.drawSources(this.sourcesService.sources(), { fitBounds: false });
    }

    public async removeConnieSource(): Promise<void> {
        if (!this.isMapPresent()) {
            return;
        }

        const connieSource = this.map.getSource(CONNIE_SOURCE_ID);

        if (!connieSource) {
            return;
        }

        try {
            const layerStyle = await this.sourcesService.getLayerStyle(null);
            layerStyle.style.forEach((mapboxStyle: MapboxStyle) => {
                if (this.map.getLayer(`${CONNIE_LAYER_ID}_${mapboxStyle.id}`)) {
                    this.map.removeLayer(`${CONNIE_LAYER_ID}_${mapboxStyle.id}`);
                }
            });
        } finally {
            this.map.removeSource(connieSource.id);
        }
    }

    public resetMapBounds(): void {
        this.mapBounds = new LngLatBounds();
    }

    public override toggleSourceVisibility(source: MapBaseSource<unknown>, isVisible?: boolean): void {
        if (!this.isMapPresent()) {
            return;
        }

        if ('isActivated' in source) {
            const layerSource = source as MapLayerSource;

            if (isVisible !== undefined) {
                layerSource.isVisible = isVisible;
                this.mapLayerService.setSourceVisibilityWithoutStyle(layerSource, isVisible);
                MapSourceService.checkSourcesVisibility.update((isVisible: boolean) => !isVisible);

                return;
            }

            this.mapLayerService.toggleSourceVisibility(layerSource);
            layerSource.isVisible = this.mapLayerService.isSourceVisible(layerSource);

            MapSourceService.checkSourcesVisibility.update((isVisible: boolean) => !isVisible);

            return;
        }

        if (source instanceof MapFieldInterventionSource) {
            this.mapFieldInterventionService.toggleSourceVisibility(source);
            MapSourceService.checkSourcesVisibility.update((isVisible: boolean) => !isVisible);

            return;
        }
    }

    public updateMapBounds(layers: Layer[]): void {
        if (!this.mapBounds) {
            return;
        }

        let hasValidBounds = false;

        for (const layer of layers) {
            if (layer.bbox?.bbox?.length !== 4) {
                continue;
            }

            AssertService.isBbox(layer.bbox.bbox);

            this.mapBounds?.extend(layer.bbox!.bbox);
            hasValidBounds = true;
        }

        if (!hasValidBounds) {
            return;
        }

        this.map!.fitBounds(this.mapBounds, MapComponent.fitBoundsOptions, { fitBounds: true });
    }

    private addGeometryCreationSource(feature: Feature): void {
        if (!this.map) {
            return;
        }

        if (this.map.getSource(MapEditModeService.CREATE_GEOMETRY_SOURCE)) {
            this.map.removeSource(MapEditModeService.CREATE_GEOMETRY_SOURCE);
        }

        this.map.addSource(MapEditModeService.CREATE_GEOMETRY_SOURCE, {
            data: feature,
            type: 'geojson',
        });
    }

    private async addMapEditSource(sourceIds: string[], entityId?: string): Promise<boolean> {
        if (this.map!.getSource(MapEditModeService.EDIT_SOURCE)) {
            this.map!.removeSource(MapEditModeService.EDIT_SOURCE);
        }

        if (this.map!.getSource(MapEditModeService.EDIT_ADD_VERTEX_SOURCE)) {
            this.map!.removeSource(MapEditModeService.EDIT_ADD_VERTEX_SOURCE);
        }

        const editLayerFeature = await this.setupVertexLayer(sourceIds, entityId);

        this.map!.addSource(MapEditModeService.EDIT_SOURCE, {
            data: {
                features: editLayerFeature.vertexLayers,
                type: 'FeatureCollection',
            },
            type: 'geojson',
        });

        this.map!.addSource(MapEditModeService.EDIT_ADD_VERTEX_SOURCE, {
            data: {
                features: editLayerFeature.plusLayers,
                type: 'FeatureCollection',
            },
            type: 'geojson',
        });

        return editLayerFeature.isLineType;
    }

    private addMapVertexIcon(): void {
        if (!this.isMapPresent()) {
            return;
        }

        this.svgIcons.forEach((svgInformation: SvgIcon) => {
            if (this.map.getImage(svgInformation.name)) {
                return;
            }

            const img = new Image();
            img.onload = (): void => {
                this.map.addImage(svgInformation.name, img, { sdf: false });
            };
            img.src = svgInformation.url;
        });
    }

    private buildPointProperties(
        i: number,
        ringIndex: number,
        multiIndex: number,
        options: EditVertexOptions,
        context: EditVertexEntityContext,
    ): Record<string, unknown> {
        return {
            [POINT_INDEX]: i,
            multiIndex: options.isRingIndex ? ringIndex : multiIndex,
            uniqueId: `${context.entityId?.length ? context.entityId : uuidv4()}-${i}`,
            ...context,
        };
    }

    private createPointFeature(ring: number[][], i: number, options: EditVertexOptions): Feature<Point> {
        const [x1, y1] = ring[i];

        if (!options.isAddPolygonVertex) {
            return point([x1, y1]);
        }

        const [x2, y2] = ring[i + 1];

        return point([(x1 + x2) / 2, (y1 + y2) / 2]);
    }

    private drawGeom(source: MapGeomSource): void {
        const geom = source.data as GeoJSON;
        AssertService.assertGeometryType(geom);

        this.addGeoJSONSource(source.sourceId, geom);
        this.addSourceStyle(source.sourceId, source.style);
        this.setSourceVisibility(source.sourceId, source.style, true);

        if (this.mapBounds) {
            this.mapBounds.extend(source.data.bbox as LngLatLike);
        }
    }

    private extractOnePointInFeature(coordinates: GeometryCoordinates): LngLat {
        let currentCoordinates: GeometryCoordinates = coordinates;

        while (typeof currentCoordinates[0] !== 'number') {
            currentCoordinates = currentCoordinates[0];
        }

        const x = currentCoordinates[0] as number;
        const y = currentCoordinates[1] as number;

        return new LngLat(x, y);
    }

    private geometryToFeaturePoints(
        layerId: string,
        layerName: string,
        entityId: string,
        geometry: Geometry,
        businessIdentifier?: string,
    ): Feature<Point, GeoJsonProperties>[] {
        const features: Feature<Point, GeoJsonProperties>[] = [];
        const options = { isAddPolygonVertex: false, isRingIndex: false, onlyFirstAndLast: false };
        const entityContext = {
            businessIdentifier,
            entityId,
            layerId,
            layerName,
        };
        let coordinates: number[][][][] = [];

        switch (geometry.type) {
            case SdkPoint.TypeEnum.POINT:
                coordinates = [[[geometry.coordinates]]];
                break;
            case MultiPoint.TypeEnum.MULTI_POINT:
            case SdkLineString.TypeEnum.LINE_STRING:
                coordinates = [[geometry.coordinates]];
                break;
            case MultiLineString.TypeEnum.MULTI_LINE_STRING:
            case Polygon.TypeEnum.POLYGON:
                options.isRingIndex = true;
                coordinates = [geometry.coordinates];
                break;
            case MultiPolygon.TypeEnum.MULTI_POLYGON:
                coordinates = geometry.coordinates;
                break;
            default:
                break;
        }

        features.push(...this.getFeaturePointByGeometry(coordinates, options, entityContext));

        return features;
    }

    private getAddVertexFeature(
        feature: Feature<Geometry, GeoJsonProperties>,
        layerId: string,
        layerName: string,
    ): {
        features: Feature<Point, GeoJsonProperties>[];
        isLineType: boolean;
    } {
        if (
            feature.geometry.type !== Polygon.TypeEnum.POLYGON &&
            feature.geometry.type !== MultiPolygon.TypeEnum.MULTI_POLYGON &&
            feature.geometry.type !== SdkLineString.TypeEnum.LINE_STRING &&
            feature.geometry.type !== MultiLineString.TypeEnum.MULTI_LINE_STRING
        ) {
            return { features: [], isLineType: false };
        }

        let coordinates: number[][][][] = [];
        let isLineType = false;
        const options = { isAddPolygonVertex: false, isRingIndex: false, onlyFirstAndLast: false };

        switch (feature.geometry.type) {
            case Polygon.TypeEnum.POLYGON:
                coordinates = [feature.geometry.coordinates];
                options.isAddPolygonVertex = true;
                break;
            case SdkLineString.TypeEnum.LINE_STRING:
                coordinates = [[feature.geometry.coordinates]];
                options.isRingIndex = true;
                options.onlyFirstAndLast = true;
                isLineType = true;
                break;
            case MultiPolygon.TypeEnum.MULTI_POLYGON:
                coordinates = feature.geometry.coordinates;
                options.isAddPolygonVertex = true;
                break;
            case MultiLineString.TypeEnum.MULTI_LINE_STRING:
                coordinates = [feature.geometry.coordinates];
                options.isRingIndex = true;
                options.onlyFirstAndLast = true;
                isLineType = true;
        }

        const features = this.getFeaturePointByGeometry(coordinates, options, {
            businessIdentifier: feature.properties?.['businessIdentifier'],
            entityId: feature.properties?.['uuid'],
            layerId,
            layerName,
        });

        return { features, isLineType };
    }

    private getEditStateGeometry(layerId: string, feature: Feature<Geometry, GeoJsonProperties>): Geometry {
        if (!feature.properties?.['uuid']) {
            return feature.geometry;
        }

        const previewGeometry = this.geometryEditPreviewService.getGeometry(layerId, feature.properties['uuid']);

        if (previewGeometry) {
            return previewGeometry as Geometry;
        }

        const state = this.editModeStateService.getEntityState(
            this.projectBloc.value()!.id,
            layerId,
            feature.properties['uuid'],
        );

        if (!state?.properties?.['geom']) {
            return feature.geometry;
        }

        return state.properties['geom'];
    }

    private getEditVertexFeature(
        acc: EditsVertex,
        feature: Feature<Geometry, GeoJsonProperties>,
        layerId: string,
        layerName: string,
    ): { acc: EditsVertex; isLineType: boolean } {
        if (feature.properties?.['uuid']) {
            feature.geometry = this.getEditStateGeometry(layerId, feature);
        }

        const addVertexFeature = this.getAddVertexFeature(feature, layerId, layerName);

        acc.plusCoordinates.push(...addVertexFeature.features);

        const featurePoints = this.geometryToFeaturePoints(
            layerId,
            layerName,
            feature.properties?.['uuid'] ?? '',
            feature.geometry,
            feature.properties?.['businessIdentifier'],
        );

        acc.vertexLayers.push(...featurePoints);

        return { acc, isLineType: addVertexFeature.isLineType };
    }

    private getEditVertexFeaturesWithProperties(
        rawFeature: Feature<Point, GeoJsonProperties>[],
    ): Feature<Point, GeoJsonProperties>[] {
        return rawFeature.map((feature: Feature<Point, GeoJsonProperties>) => ({
            geometry: {
                coordinates: feature.geometry.coordinates,
                type: 'Point',
            },
            properties: feature.properties,
            type: 'Feature',
        }));
    }

    private getFeaturePointByGeometry(
        multiCoordinates: number[][][][],
        options: EditVertexOptions,
        context: EditVertexEntityContext,
    ): Feature<Point>[] {
        const points: Feature<Point>[] = [];

        multiCoordinates.forEach((coordinates, multiIndex) => {
            coordinates.forEach((ring, ringIndex) => {
                points.push(...this.processRing(ring, ringIndex, multiIndex, options, context));
            });
        });

        return points;
    }

    private processRing(
        ring: number[][],
        ringIndex: number,
        multiIndex: number,
        options: EditVertexOptions,
        context: EditVertexEntityContext,
    ): Feature<Point>[] {
        const points: Feature<Point>[] = [];

        const limit = ring.length - (options.isAddPolygonVertex ? 1 : 0);

        for (let i = 0; i < limit; i++) {
            if (this.shouldSkipPoint(i, ring.length, options)) {
                continue;
            }

            const pointFeature = this.createPointFeature(ring, i, options);

            const bounds = this.map!.getBounds();

            if (!bounds.contains([pointFeature.geometry.coordinates[0], pointFeature.geometry.coordinates[1]])) {
                continue;
            }

            pointFeature.properties = this.buildPointProperties(i, ringIndex, multiIndex, options, context);

            if (options.onlyFirstAndLast) {
                pointFeature.properties['isLineType'] = true;
            }

            points.push(pointFeature);
        }

        return points;
    }

    private async setupVertexLayer(
        sourceIds: string[],
        entityId?: string,
    ): Promise<{
        isLineType: boolean;
        plusLayers: Feature<Point, GeoJsonProperties>[];
        vertexLayers: Feature<Geometry, GeoJsonProperties>[];
    }> {
        if (!this.projectBloc.value()) {
            throw new Error('Project should be defined');
        }

        const sourcesWithEditStyle: MapBaseSource<unknown>[] = this.sourcesService
            .sources()
            .filter((source) => sourceIds.includes(source.sourceId));

        let vertexPointLocations: Feature<Point, GeoJsonProperties>[] = [];
        let plusPointLocations: Feature<Point, GeoJsonProperties>[] = [];
        let isLineType = false;

        for (const source of sourcesWithEditStyle) {
            const mapSource: GeoJSONSource | undefined = this.map!.getSource(source.sourceId) as GeoJSONSource;

            if (!mapSource) {
                continue;
            }

            const geoJson: FeatureCollection = (await mapSource.getData()) as FeatureCollection;
            let filteredFeatures = geoJson.features;

            if (entityId) {
                filteredFeatures = geoJson.features.filter((feature) => feature.properties?.['uuid'] === entityId);
            }

            const layerFilterResult = this.mapFilterService
                .appliedFiltersResult()
                .find((layerFilterResult: LayerFilterResult) => layerFilterResult.layerId === source.sourceId);

            if (layerFilterResult) {
                filteredFeatures = filteredFeatures.filter((feature: Feature<Geometry, GeoJsonProperties>) =>
                    layerFilterResult.entityIds.includes(feature.properties?.['uuid'] ?? ''),
                );
            }

            const features = filteredFeatures.reduce(
                (acc: EditsVertex, feature: Feature<Geometry, GeoJsonProperties>) => {
                    const editVertexFeatures = this.getEditVertexFeature(acc, feature, source.sourceId, source.name);
                    isLineType = editVertexFeatures.isLineType;

                    return editVertexFeatures.acc;
                },
                { plusCoordinates: [], vertexLayers: [] },
            );

            vertexPointLocations = [...features.vertexLayers, ...vertexPointLocations];

            plusPointLocations = [...features.plusCoordinates, ...plusPointLocations];
        }

        return {
            isLineType,
            plusLayers: this.getEditVertexFeaturesWithProperties(plusPointLocations),
            vertexLayers: this.getEditVertexFeaturesWithProperties(vertexPointLocations),
        };
    }

    private shouldSkipPoint(i: number, ringLength: number, options: EditVertexOptions): boolean {
        if (!options.onlyFirstAndLast) {
            return false;
        }

        return i !== 0 && i !== ringLength - 1;
    }
}
